import { useState } from "react";

export default function TopBar({
  theme, toggleTheme,
  activePage, setActivePage,
  isProcessing, processingLabel,
  user, onLogout,
  isMobile, onMenuClick,
}) {
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const displayName = user?.username || user?.name || user?.email || "User";

  const NAV = [
    { id: "workspace",  icon: "dashboard",        label: "Workspace"  },
    { id: "syllabus",   icon: "import_contacts",  label: "Syllabus"   },
    { id: "youtube",    icon: "smart_display",    label: "YouTube"    }, // ← NEW
    { id: "exam",       icon: "edit_note",         label: "Take Exam"  },
    { id: "progress",   icon: "bar_chart",         label: "Progress"   },
  ];

  const tabColor = {
    workspace: "var(--accent-2)",
    syllabus:  "var(--purple, #a855f7)",
    youtube:   "var(--rose, #ef4444)",      // ← YouTube red
    exam:      "var(--amber)",
    progress:  "var(--green)",
  };

  return (
    <header className="app-topbar" style={{
      height: "var(--header-h)",
      background: "var(--surface)",
      borderBottom: "1px solid var(--border)",
      display: "flex",
      alignItems: "center",
      padding: "0 14px",
      gap: 8,
      flexShrink: 0,
      position: "relative",
      zIndex: 1000,
      minWidth: 0,
      overflow: "visible",
    }}>
      {isProcessing && (
        <div className="processing-bar" style={{ position: "absolute", bottom: -1, left: 0, right: 0 }} />
      )}

      {/* Hamburger — opens the notebook drawer on mobile */}
      <button
        onClick={onMenuClick}
        className="btn btn-ghost btn-icon mobile-only"
        title="Menu"
        style={{ marginRight: 2 }}
      >
        <span className="material-symbols-rounded" style={{ fontSize: 20 }}>menu</span>
      </button>

      {/* Brand */}
      <div className="topbar-brand" style={{ display: "flex", alignItems: "center", gap: 8, marginRight: 6, minWidth: 0, flexShrink: 0 }}>
        <div style={{
          width: 30, height: 30, borderRadius: 9,
          background: "linear-gradient(135deg, var(--accent), var(--sky))",
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0, boxShadow: "0 2px 8px var(--accent-glow)",
        }}>
          <span className="material-symbols-rounded" style={{ fontSize: 17, color: "white", fontVariationSettings: "'FILL' 1" }}>
            psychology
          </span>
        </div>
        {!isMobile && (
          <span style={{
            fontFamily: "var(--font-display)", fontWeight: 700,
            fontSize: 15, letterSpacing: 0, color: "var(--ink)",
            whiteSpace: "nowrap",
          }}>
            Study<span style={{ color: "var(--accent-2)" }}>Mind</span>
          </span>
        )}
      </div>

      {!isMobile && (
        <div style={{ width: 1, height: 20, background: "var(--border)", flexShrink: 0 }} />
      )}

      {/* Nav tabs — icon-only & horizontally scrollable on mobile */}
      <nav className="topbar-nav" style={{
        display: "flex", gap: 2,
        overflowX: "auto", minWidth: 0,
        flex: "1 1 auto",
        scrollbarWidth: "none",
      }}>
        {NAV.map(n => {
          const isActive = activePage === n.id;
          return (
            <button
              key={n.id}
              onClick={() => setActivePage(n.id)}
              title={n.label}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: isMobile ? "7px 9px" : "6px 10px",
                borderRadius: "var(--radius-md)",
                fontSize: 13, fontWeight: 500,
                color: isActive ? "var(--ink)" : "var(--ink-3)",
                background: isActive ? "var(--surface-3)" : "transparent",
                border: "1px solid",
                borderColor: isActive ? "var(--border-2)" : "transparent",
                transition: "all var(--transition)",
                flex: "0 0 auto",
                maxWidth: 132,
              }}
            >
              <span className="material-symbols-rounded" style={{
                fontSize: 16,
                color: isActive ? tabColor[n.id] : "inherit",
                fontVariationSettings: isActive ? "'FILL' 1" : "'FILL' 0",
              }}>
                {n.icon}
              </span>
              {!isMobile && <span className="topbar-tab-label">{n.label}</span>}
            </button>
          );
        })}
      </nav>

      {/* AI processing pill */}
      {isProcessing && (
        <div className="ai-indicator animate-fadeIn desktop-only">
          <span className="material-symbols-rounded" style={{ fontSize: 14, fontVariationSettings: "'FILL' 1", color: "var(--accent-2)" }}>
            smart_toy
          </span>
          <span>{processingLabel || "AI thinking…"}</span>
          <div className="dots">
            <div className="dot" /><div className="dot" /><div className="dot" />
          </div>
        </div>
      )}

      <button onClick={toggleTheme} className="btn btn-ghost btn-icon" title="Toggle theme">
        <span className="material-symbols-rounded" style={{ fontSize: 18 }}>
          {theme === "dark" ? "light_mode" : "dark_mode"}
        </span>
      </button>

      <div style={{ position: "relative", zIndex: 1100, flexShrink: 0 }}>
        <button
          onClick={() => setUserMenuOpen(o => !o)}
          style={{
            width: 30, height: 30, borderRadius: "50%",
            background: "linear-gradient(135deg, var(--accent), var(--blue))",
            border: "2px solid var(--border-2)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 12, fontWeight: 600, color: "white", cursor: "pointer",
          }}
          title={displayName}
        >
          {(displayName?.[0] || "U").toUpperCase()}
        </button>

        {userMenuOpen && (
          <div className="animate-scaleIn" style={{
            position: "absolute", top: "calc(100% + 8px)", right: 0,
            background: "var(--surface)", border: "1px solid var(--border-2)",
            borderRadius: "var(--radius-xl)", padding: 6,
            minWidth: 180, boxShadow: "var(--shadow-lg)", zIndex: 2000,
          }}>
            <div style={{ padding: "8px 10px 10px", borderBottom: "1px solid var(--border)", marginBottom: 4 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>{displayName}</div>
              <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 2 }}>Free plan</div>
            </div>
            <button
              onClick={() => { setUserMenuOpen(false); onLogout?.(); }}
              className="btn btn-ghost"
              style={{ width: "100%", justifyContent: "flex-start", fontSize: 13, padding: "7px 10px", color: "var(--rose)", gap: 8 }}
            >
              <span className="material-symbols-rounded" style={{ fontSize: 16 }}>logout</span>
              Sign out
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
